import tinycolor, { Instance as TinyColor } from 'tinycolor2'
import { memo, useCallback, useMemo, useState } from "react"
import { defaultSwatches } from '../colorSelector.constants'
import { Box, Divider, Popover, PopoverProps, Stack, Typography } from '@mui/material'
import PredefinedSwatches from '../predefinedSwatches/predefinedSwatches'
import useStyles from './styles'
import RecentSwatches from '../recentSwatches/recentSwatches'
import OpacitySlider from '../opacitySlider/opacitySlider'
import CustomColorSelector from '../customColorSelector/customColorSelector'


export type ColorPopoverProps = {
    onChange: (color: TinyColor) => void,
    value: TinyColor,
    swatches?: string[],
    recentSwatches?: string[],
    disableSwatches?: boolean
    anchorEl: PopoverProps['anchorEl'],
    onAddSwatch?: (color: TinyColor) => void,
    onClose: () => void
}

const ColorPopover: React.FC<ColorPopoverProps> = ({
    value, 
    swatches = defaultSwatches,
    disableSwatches,
    onChange,
    onClose,
    recentSwatches = [],
    anchorEl,
    onAddSwatch
}) => {

    const {classes} = useStyles({color: value})
    const predefinedSwatches = useMemo(() => swatches.map(sw => tinycolor(sw)), [swatches])
    const recentSwatchesList = useMemo(() => recentSwatches.map(sw => tinycolor(sw)), [recentSwatches])

    const [customSelectionMode, setCustomSelectionMode] = useState(false);

    const onOpacityChange = useCallback((opacity: number) => {
        onChange(value.clone().setAlpha(opacity))
    }, [value, onChange])

    const onColorSelect = useCallback((color: TinyColor) => {
        onChange(color.clone().setAlpha(value.getAlpha()))
    }, [value, onChange])

    const onAddNewClick = useCallback(() => {
        setCustomSelectionMode(true)
    }, [])

    const addSwatchHandler = useCallback((color: TinyColor) => {
        onAddSwatch && onAddSwatch(color)
        setCustomSelectionMode(false)
    }, [onAddSwatch])
    const closeCustomSelectionHandler = useCallback(() => {
        setCustomSelectionMode(false)
    }, [])

    return (
        <Popover open anchorEl={anchorEl}
            slotProps={{
                paper: {
                    className: classes.container
                }
            }}
            onClose={onClose}
        >
            <Box>
                <Stack direction="row" gap={1}>
                    <div className={classes.thumbnail} />
                    <Typography variant="body1" className={classes.colorDisplay}>{value.toHex8String()}</Typography>
                </Stack>
                <Divider  className={classes.divider} />
                {!customSelectionMode && (
                    <>
                        <PredefinedSwatches value={value} swatches={predefinedSwatches} onColorSelect={onColorSelect}  />
                        <Divider  className={classes.divider} />
                        <RecentSwatches value={value} swatches={recentSwatchesList} onColorSelect={onColorSelect} onAddNewClick={onAddNewClick} onNewSwatchAdded={() => {}} />
                    </>)}
                {customSelectionMode && (
                    <>
                        <CustomColorSelector value={value.clone().setAlpha(1)} onColorSelect={onColorSelect} onAddSwatch={addSwatchHandler} onClose={closeCustomSelectionHandler} />
                    </>
                )}
                <Divider  className={classes.divider} />
                <OpacitySlider value={value} onOpacityChange={onOpacityChange}/>
                
            </Box>
        </Popover>
    );
}

export default memo(ColorPopover)